<template>
  <div class="dashboard-layout">
    <SidebarMenu
        :is-collapsed="isSidebarCollapsed"
        :user-id="userId"
        :display-name="displayName"
        :picture-url="pictureUrl"
        @toggle-request="toggleSidebar" />

    <main class="main-content">
      <header class="content-header">
        <div class="header-title-group">
          <h1>{{ currentRouteTitle }}</h1>
          <p>「優豆代儲」後台指揮中心</p>
        </div>
      </header>

      <div class="content-body" :class="{ 'empty-router-view': isRouterViewEmpty }">
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </div>
    </main>
  </div>
</template>

<script>
import SidebarMenu from './SidebarMenu.vue';
import { getMemberById } from '@/api/auth.api.js';
import jinxImage from '@/assets/jinx.jpg'; // 1. 導入圖片

export default {
  name: 'HomeView',
  components: {
    SidebarMenu,
  },
  data() {
    return {
      userId: '',
      pictureUrl: '',
      isSidebarCollapsed: false,
      user: {},
      displayName: '',
      jinxImageUrl: jinxImage, // 2. 將圖片 URL 存儲在 data 中 (可選，主要用於 CSS 變數)
    };
  },
  computed: {
    currentRouteTitle() {
      return this.$route.meta.title || '優豆代儲';
    },
    // 3. 判斷 router-view 是否為空
    isRouterViewEmpty() {
      const homeRouteMatch = this.$route.matched.find(m => m.name === 'Home');
      if (homeRouteMatch) {
        return this.$route.name === 'Home';
      }
      if (this.$route.matched.length > 0) {
        return this.$route.path === (homeRouteMatch ? homeRouteMatch.path : '/');
      }
      return true; // 預設為空
    }
  },
  async created() {
    const token = localStorage.getItem('jwt');
    if (!token) {
      this.$router.push('/login');
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      this.userId = payload.sub || '未知指揮官';
    } catch (e) {
      console.error('登入資料無法解析。');
      localStorage.removeItem('jwt');
      this.$router.push('/login');
      return;
    }
    try {
      this.user = await getMemberById(this.userId);
      this.pictureUrl = this.user?.pictureUrl ?? '';
      this.displayName = this.user?.displayName ?? '管理員';
    } catch (error) {
      // A missing/unavailable optional profile must not discard a valid admin session.
      this.displayName = '管理員';
      if (error.response?.status === 401) {
        localStorage.removeItem('jwt');
        this.$router.push('/login');
      }
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    }
  }
};
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background-color: #121212;
  background-image:
      radial-gradient(ellipse at top left, rgba(80, 20, 80, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse at bottom right, rgba(30, 80, 30, 0.2) 0%, transparent 50%),
      linear-gradient(to bottom right, rgba(10, 10, 15, 0.9) 0%, rgba(15, 15, 25, 0.95) 70%, #0a0a0a 100%);
  color: #e0e0e0;
  font-family: 'Roboto', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-x: hidden;
}

.main-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  transition: margin-left 0.3s ease-in-out;
}

.content-header {
  display: flex;
  align-items: center;
  padding: 20px 30px;
  background-color: rgba(30, 30, 40, 0.6);
  box-shadow: 0 2px 10px rgba(0,0,0,0.5);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}

.header-title-group {
  flex-grow: 1;
  text-align: left;
}

.header-title-group h1 {
  font-size: 1.8em;
  color: #ff8c69;
  margin: 0 0 4px 0;
  font-weight: 700;
  text-shadow: 0 0 8px rgba(255, 140, 105, 0.4);
}

.header-title-group p {
  font-size: 0.95em;
  color: #a0a0b0;
  margin: 0;
}

.content-body {
  padding: 30px 40px;
  font-size: 1.1em;
  line-height: 1.7;
  color: #c0c0c0;
  flex-grow: 1;
  overflow-y: auto;
  position: relative; /* 為了偽元素定位 */
}

/* 4. 新增樣式來顯示背景圖片 */
.content-body.empty-router-view {
  background-image: url('@/assets/jinx.jpg');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover; /* 或 contain，取決於您希望圖片如何縮放 */
  /* 根據圖片 1215x717 的比例，可以考慮用 contain 並輔助背景色 */
  /* background-size: contain; */
  /* background-color: #0f0f17; */ /* 如果用 contain，可以加一個背景色 */
}

/* 如果您想讓背景圖不那麼突兀，可以加一個遮罩 */
.content-body.empty-router-view::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(18, 18, 18, 0.6); /* 半透明黑色遮罩 */
  z-index: -1; /* 確保在內容之下，背景之上 (如果背景圖是直接在 .content-body 上) */
  /* 如果背景圖是偽元素，這裡的 z-index 可能需要調整 */
}
/* 或者讓 router-view 內容優先顯示 */
.content-body.empty-router-view > router-view:not(:empty) ~ * { /* 這是一個嘗試，但效果可能不佳 */
  /* display: none; */
}


@media (max-width: 768px) {
  .content-header {
    padding: 15px 20px;
  }
  .header-title-group h1 {
    font-size: 1.6em;
  }
  .content-body {
    padding: 20px;
  }
}
</style>
